import React, { useState, useMemo, useEffect } from 'react';
import MetricChart from './EstadisticasMetricChartDiario.tsx';
import HeaderCustomized from "../imgBackground/HeaderCustomized";
import SelectorAnioMes from './SelectorAnioMes.tsx';

// Util: clave compuesta estable
const makeKey = (it: any) =>
  `${(it.provincia ?? '').trim()}||${(it.distrito ?? '').trim()}||${(it.colegio ?? '').trim()}||${(it.nivel ?? '').trim()}`;

// Componente Principal Integrado
export default function EstadisticasLectoresMapEachDay({user}) {
  const nomProv = user.attributes['zoneinfo']

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = String(now.getMonth() + 1).padStart(2, "0");
  const defaultYearMonth = `${currentYear}_${currentMonth}`;


  // ★ ahora guardamos la clave compuesta, no solo el nombre
  const [colegioKeySeleccionado, setColegioKeySeleccionado] = useState<string | null>(null);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metricFilter, setMetricFilter] = useState<'IGL' | 'ICP' | 'INorm_Promedio' | 'Cobertura_Lectores' | 'Lecturas_Promedio' | 'Usuarios_Activos'>('IGL');
  const [viewType, setViewType] = useState<'line' | 'bar'>('line');
  const [anioMesSeleccionado, setAnioMesSeleccionado] = useState(defaultYearMonth);


  // Cargar datos
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://img-dinoblanco-shs.s3.us-east-1.amazonaws.com/c/r/res_colegios_diario_del_mes.json');
        if (!response.ok) throw new Error('Error al cargar datos');
        const jsonData = await response.json();
        setData(jsonData ?? []);
      } catch (err: any) {
        setError(err.message ?? 'Error desconocido');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // ★ Lista de colegios ÚNICOS por clave compuesta (dentro de UGEL; elimina el filtro si quieres global)
  const colegios = useMemo(() => {
    const m = new Map<string, { key: string; colegio: string; nivel: string; distrito: string; provincia: string }>();
    for (const it of data) {
      if (it.provincia !== nomProv) continue; // ← quita esto si no quieres restringir a UGEL
      const key = makeKey(it);
      if (!m.has(key)) {
        m.set(key, {
          key,
          colegio: it.colegio ?? '',
          nivel: it.nivel ?? '',
          distrito: it.distrito ?? '',
          provincia: it.provincia ?? '',
        });
      }
    }
    // Ordena por colegio, luego distrito, luego nivel para agrupar visualmente
    return Array.from(m.values()).sort((a, b) =>
      a.colegio.localeCompare(b.colegio) ||
      a.distrito.localeCompare(b.distrito) ||
      a.nivel.localeCompare(b.nivel)
    );
  }, [data]);

  // ★ Procesar datos para el dashboard usando la clave compuesta seleccionada
  const { datosProcesados, estadisticas, contextoSeleccion } = useMemo(() => {
    
    if (!data.length) {
      return { datosProcesados: [] as any[], estadisticas: {} as any, contextoSeleccion: null as null | any };
    }

    // Base filtrada por provincia (mantiene tu lógica original)
    let base = data.filter((it) => it.provincia === nomProv); // ← quita esto si no quieres restringir

    // Si hay selección, filtra exactamente por la misma clave compuesta
    let contextoSeleccion: null | { colegio: string; nivel: string; distrito: string; provincia: string } = null;
    if (colegioKeySeleccionado) {
      base = base.filter((it) => makeKey(it) === colegioKeySeleccionado);
      // Extract para mostrar encabezado limpio
      const anyIt = base[0];
      if (anyIt) {
        contextoSeleccion = {
          colegio: anyIt.colegio ?? '',
          nivel: anyIt.nivel ?? '',
          distrito: anyIt.distrito ?? '',
          provincia: anyIt.provincia ?? '',
        };
      }
    }

    // Agregación por día (tu lógica original), luego el MetricChart densifica los días faltantes
    const [anio, mes]  = anioMesSeleccionado.split("_")
    const baseFiltrada = base.filter(it => 
      String(it.anio) === String(anio) && 
      String(it.mes) === String(mes)
    );
    console.log(baseFiltrada)
    console.log(anioMesSeleccionado)
    const acumulado: Record<number, any> = {};
    for (const it of baseFiltrada) {
      const dia = Math.floor(Number(it.dia_leido_del_mes ?? it.dia ?? 0));
      if (!acumulado[dia]) {
        acumulado[dia] = {
          dia,
          ICP: 0,
          IGL: 0,
          INorm_Promedio: 0,
          Cobertura_Lectores: 0,
          Lecturas_Promedio: 0,
          Usuarios_Activos: 0,
          count: 0,
        };
      }
      acumulado[dia].ICP += Number(it.ICP ?? 0);
      acumulado[dia].IGL += Number(it.IGL ?? 0);
      acumulado[dia].INorm_Promedio += Number(it.INorm_Promedio ?? 0);
      acumulado[dia].Cobertura_Lectores += Number(it['Cobertura_Lectores_%'] ?? 0);
      acumulado[dia].Lecturas_Promedio += Number(it.Lecturas_Promedio ?? 0);
      acumulado[dia].Usuarios_Activos = acumulado[dia].Usuarios_Activos < Number(it.Usuarios_Activos ?? 0) ? Number(it.Usuarios_Activos ?? 0) : acumulado[dia].Usuarios_Activos ;
      acumulado[dia].Usuarios_Totales = Number(it.Usuarios_Totales ?? 0);
      acumulado[dia].count += 1;
    }

    const porDia = Object.values(acumulado)
      .map((d: any) => ({
        ...d,
        ICP: d.count ? d.ICP / d.count : 0,
        IGL: d.count ? d.IGL / d.count : 0,
        INorm_Promedio: d.count ? d.INorm_Promedio / d.count : 0,
        Cobertura_Lectores: d.count ? d.Cobertura_Lectores / d.count : 0,
        Lecturas_Promedio: d.count ? d.Lecturas_Promedio / d.count : 0,
        Usuarios_Activos: d.Usuarios_Activos ? Math.round(d.Usuarios_Activos ) : 0,
        Usuarios_Totales: d.Usuarios_Totales ? Math.round(d.Usuarios_Totales ) : 0,
      }))
      .sort((a, b) => a.dia - b.dia);

    const ultimo = porDia[porDia.length - 1];
    const primero = porDia[0];

    const estadisticas = {
      totalDias: porDia.length,
      totalAlumnos: primero? Number(porDia[0].Usuarios_Totales):0,
      totalActivos: ultimo? Number(ultimo.Usuarios_Activos):0,
      progresoIGL:
        ultimo && primero ? ((ultimo.IGL - primero.IGL) * 100).toFixed(1) : '0.0',
      tendencia:
        ultimo && primero
          ? ultimo.IGL > primero.IGL
            ? '📈 Ascendente'
            : ultimo.IGL < primero.IGL
            ? '📉 Descendente'
            : '➡️ Estable'
          : '➡️ Estable',
    };

    return { datosProcesados: porDia, estadisticas, contextoSeleccion };
  }, [data, colegioKeySeleccionado,anioMesSeleccionado ]);

  // Selector de Colegios
  const SelectorColegios = () => {
    const [busqueda, setBusqueda] = useState('');
    const [filtroNivel, setFiltroNivel] = useState<'todos' | string>('todos');
    const [filtroDistrito, setFiltroDistrito] = useState<'todos' | string>('todos');

    const { colegiosFiltrados, niveles, distritos } = useMemo(() => {
      const nivelesSet = new Set<string>();
      const distritosSet = new Set<string>();

      for (const c of colegios) {
        if (c.nivel) nivelesSet.add(c.nivel);
        if (c.distrito) distritosSet.add(c.distrito);
      }

      const filtrados = colegios.filter((c) => {
        const coincideBusqueda = c.colegio.toLowerCase().includes(busqueda.toLowerCase());
        const coincideNivel = filtroNivel === 'todos' || c.nivel === filtroNivel;
        const coincideDistrito = filtroDistrito === 'todos' || c.distrito === filtroDistrito;
        return coincideBusqueda && coincideNivel && coincideDistrito;
      });

      return {
        colegiosFiltrados: filtrados,
        niveles: ['todos', ...Array.from(nivelesSet).sort()],
        distritos: ['todos', ...Array.from(distritosSet).sort()],
      };
    }, [colegios, busqueda, filtroNivel, filtroDistrito]);

    if (loading) {
      return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando colegios...</p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm h-fit">

        <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4">
          <h3 className="text-lg font-bold text-gray-900">Seleccionar Colegio</h3>
          <p className="text-sm text-gray-600">{colegios.length} Instituciones (colegio/nivel/distrito) en {nomProv}</p>
        </div>

        <div className="border-b border-gray-200 p-4 bg-gray-50">
          <div className="space-y-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">🔍</div>
              <input
                type="text"
                placeholder="Buscar colegio..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select
                value={filtroNivel}
                onChange={(e) => setFiltroNivel(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                {niveles.map((nivel) => (
                  <option key={nivel} value={nivel}>
                    {nivel === 'todos' ? '🎓 Todos' : nivel}
                  </option>
                ))}
              </select>
              <select
                value={filtroDistrito}
                onChange={(e) => setFiltroDistrito(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                {distritos.map((distrito) => (
                  <option key={distrito} value={distrito}>
                    {distrito === 'todos' ? '📍 Todos' : distrito}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

  
        {colegioKeySeleccionado && (
          <div className="border-t  border-b border-blue-300  px-6 py-4 relative shadow-sm bg-green-300">
            {/* Borde superior de acento */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-500"></div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {/* Icono con contenedor */}
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center border border-blue-200">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                </div>

                {/* Información del colegio */}
                <div className="min-w-0 flex-1 ">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-sm font-semibold text-gray-900">
                      Colegio Seleccionado
                    </span>
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    {(() => {
                      const sel = colegios.find((c) => c.key === colegioKeySeleccionado);
                      return sel ? (
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="font-medium text-gray-900 bg-blue-50 px-2 py-1 rounded-md border border-blue-100">
                            {sel.colegio}
                          </span>
                          <span className="text-gray-400">•</span>
                          <span className="text-gray-700">{sel.nivel}</span>
                          <span className="text-gray-400">•</span>
                          <span className="text-gray-700">{sel.distrito}</span>
                          <span className="text-gray-400">•</span>
                          <span className="text-gray-700">{sel.provincia}</span>
                        </div>
                      ) : (
                        <span className="text-gray-500 italic">— Colegio no encontrado</span>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Botón de limpiar */}
              <button
                onClick={() => setColegioKeySeleccionado(null)}
                className="group flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 border border-gray-200 hover:border-red-300 flex-shrink-0 hover:shadow-sm"
              >
                <svg className="w-4 h-4 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="font-medium">Limpiar</span>
              </button>
            </div>
          </div>
        )}

        <div className="max-h-96 overflow-y-auto">
          {colegiosFiltrados.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <div className="text-4xl mb-4">🔍</div>
              <p>No se encontraron colegios</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {colegiosFiltrados.map((c) => {
                const active = colegioKeySeleccionado === c.key;
                return (
                  <div
                    key={c.key} // ★ usa la clave compuesta
                    className={`p-4 cursor-pointer transition-all hover:bg-blue-50 group ${
                      active ? 'bg-blue-100 border-l-4 border-l-blue-500' : 'hover:border-l-4 hover:border-l-blue-300'
                    }`}
                    onClick={() => setColegioKeySeleccionado(c.key)} // ★ guarda clave compuesta
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900 truncate">{c.colegio}</h4>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              c.nivel === 'INICIAL'
                                ? 'bg-pink-100 text-pink-800'
                                : c.nivel === 'PRIMARIA'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {c.nivel}
                          </span>
                          <span className="text-xs text-gray-600">📍 {c.distrito}</span>
                          <span className="text-xs text-gray-500">🗺️ {c.provincia}</span>
                        </div>
                      </div>
                      <div className={`ml-4 ${active ? 'text-blue-600' : 'text-gray-400'}`}>{active ? '✓' : '→'}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>


      </div>
    );
  };

  // Dashboard de Progreso
  const DashboardProgreso = () => {
    const metricas = {
      IGL: { label: 'Índice Global Lector', color: 'bg-purple-500', format: (v: number) => (v * 100).toFixed(1) + '%' },
      ICP: { label: 'Índice Comprensión Lectora', color: 'bg-blue-500', format: (v: number) => (v * 100).toFixed(1) + '%' },
      INorm_Promedio: { label: 'Índice de Hábito Lector', color: 'bg-green-500', format: (v: number) => v.toFixed(3) },
      Cobertura_Lectores: { label: 'Cobertura Lectores', color: 'bg-orange-500', format: (v: number) => v.toFixed(1) + '%' },
      Lecturas_Promedio: { label: 'Lecturas por Día', color: 'bg-red-500', format: (v: number) => v.toFixed(1) },
      Usuarios_Activos: { label: 'Usuarios Activos', color: 'bg-indigo-500', format: (v: number) => Math.round(v).toString() },
    } as const;

    const maxValor =
      datosProcesados.length > 0 ? Math.max(...datosProcesados.map((d: any) => Number(d[metricFilter] ?? 0))) : 1;

    if (loading) {
      return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-600">Cargando datos...</h3>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-gray-600">Error al cargar datos</h3>
          <p className="text-gray-500">{error}</p>
        </div>
      );
    }

    /* if (datosProcesados.length === 0) {
      return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
          <div className="text-gray-400 text-4xl mb-4">📊</div>
          <h3 className="text-lg font-semibold text-gray-600">No hay datos disponibles</h3>
          <p className="text-gray-500">Selecciona un colegio para ver su progreso</p>
        </div>
      );
    } */

    const encabezado =
      contextoSeleccion
        ? `${contextoSeleccion.colegio} · ${contextoSeleccion.nivel} · ${contextoSeleccion.distrito} · ${contextoSeleccion.provincia}`
        : `Todos (${nomProv})`;

    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">

        <div className="border-b border-gray-200 bg-gray-50 px-2 py-2">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{encabezado}</h1>
            </div>
            <div className="flex items-center gap-3">
              
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewType('line')}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${viewType === 'line' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
                >
                  📈
                </button>
                <button
                  onClick={() => setViewType('bar')}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${viewType === 'bar' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
                >
                  📊
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-1 border-b border-gray-200">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{estadisticas.totalDias}</div>
            <div className="text-xs text-gray-500">Días</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{colegios.length}</div>
            <div className="text-xs text-gray-500">Colegios (cantidad)</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{estadisticas.progresoIGL}%</div>
            <div className="text-xs text-gray-500">Progreso IGL</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">{estadisticas.tendencia}</div>
            <div className="text-xs text-gray-500">Tendencia</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{estadisticas.totalActivos} </div>
            <div className="text-xs text-gray-500">Total Activos</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">{estadisticas.totalAlumnos}</div>
            <div className="text-xs text-gray-500">Total Estudiantes</div>
          </div>
          
        </div>




        





                {/* Selector de Métricas - Versión Botones */}
<div className="border-t border-gray-200 bg-white px-2 py-1">
  <div className="flex items-center justify-between mb-2">
    <h3 className="text-sm font-semibold text-gray-900 flex items-center space-x-2">
      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
      <span>Selecciona una Métrica</span>
    </h3>
    <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
      {metricas[metricFilter]?.label || "Selecciona una métrica"}
    </div>
  </div>

  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
    {/* IGL */}
    <button
      onClick={() => setMetricFilter('IGL')}
      className={`group relative p-1 rounded-lg border-2 transition-all duration-200 text-left ${
        metricFilter === 'IGL'
          ? 'border-purple-500 bg-purple-50 shadow-sm'
          : 'border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-25'
      }`}
    >
      <div className="flex items-center space-x-3">
        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
          metricFilter === 'IGL' ? 'bg-purple-500' : 'bg-purple-400'
        }`}></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <span className={`text-sm font-medium ${
              metricFilter === 'IGL' ? 'text-purple-900' : 'text-gray-900'
            }`}>IGL</span>
            <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
            <span className="text-xs text-purple-600 font-medium">General</span>
          </div>
          <p className="text-xs text-gray-600 truncate">Índice Global Lector</p>
        </div>
        {metricFilter === 'IGL' && (
          <svg className="w-4 h-4 text-purple-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )}
      </div>
      
      {/* Tooltip */}
      <div className="absolute invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 z-10">
        <div className="bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg">
          <div className="font-semibold text-white mb-1 flex items-center space-x-1">
            <span>📊 Índice Global Lector</span>
          </div>
          <p className="text-gray-200 leading-relaxed">
            Puntuación general que combina comprensión lectora y hábito de lectura
          </p>
          <div className="flex items-center space-x-2 mt-2 pt-2 border-t border-gray-700">
            <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
            <span className="text-gray-300">Escala: 0-100%</span>
          </div>
        </div>
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
      </div>
    </button>

    {/* ICP */}
    <button
      onClick={() => setMetricFilter('ICP')}
      className={`group relative p-3 rounded-lg border-2 transition-all duration-200 text-left ${
        metricFilter === 'ICP'
          ? 'border-blue-500 bg-blue-50 shadow-sm'
          : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-25'
      }`}
    >
      <div className="flex items-center space-x-3">
        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
          metricFilter === 'ICP' ? 'bg-blue-500' : 'bg-blue-400'
        }`}></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <span className={`text-sm font-medium ${
              metricFilter === 'ICP' ? 'text-blue-900' : 'text-gray-900'
            }`}>ICP</span>
            <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
            <span className="text-xs text-blue-600 font-medium">Comprensión</span>
          </div>
          <p className="text-xs text-gray-600 truncate">Comprensión Lectora</p>
        </div>
        {metricFilter === 'ICP' && (
          <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )}
      </div>
      
      {/* Tooltip */}
      <div className="absolute invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 z-10">
        <div className="bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg">
          <div className="font-semibold text-white mb-1 flex items-center space-x-1">
            <span>🧠 Comprensión Lectora</span>
          </div>
          <p className="text-gray-200 leading-relaxed">
            Capacidad de entender y analizar textos. Promedio de puntuaciones en lecturas
          </p>
          <div className="flex items-center space-x-2 mt-2 pt-2 border-t border-gray-700">
            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
            <span className="text-gray-300">Escala: 0-100%</span>
          </div>
        </div>
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
      </div>
    </button>

    {/* INorm_Promedio */}
    <button
      onClick={() => setMetricFilter('INorm_Promedio')}
      className={`group relative p-3 rounded-lg border-2 transition-all duration-200 text-left ${
        metricFilter === 'INorm_Promedio'
          ? 'border-green-500 bg-green-50 shadow-sm'
          : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-25'
      }`}
    >
      <div className="flex items-center space-x-3">
        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
          metricFilter === 'INorm_Promedio' ? 'bg-green-500' : 'bg-green-400'
        }`}></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <span className={`text-sm font-medium ${
              metricFilter === 'INorm_Promedio' ? 'text-green-900' : 'text-gray-900'
            }`}>Hábito</span>
            <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
            <span className="text-xs text-green-600 font-medium">Frecuencia</span>
          </div>
          <p className="text-xs text-gray-600 truncate">Hábito Lector</p>
        </div>
        {metricFilter === 'INorm_Promedio' && (
          <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )}
      </div>
      
      {/* Tooltip */}
      <div className="absolute invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 z-10">
        <div className="bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg">
          <div className="font-semibold text-white mb-1 flex items-center space-x-1">
            <span>📚 Hábito Lector</span>
          </div>
          <p className="text-gray-200 leading-relaxed">
            Frecuencia y consistencia en la lectura. Mide el desarrollo del hábito lector
          </p>
          <div className="flex items-center space-x-2 mt-2 pt-2 border-t border-gray-700">
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span className="text-gray-300">Escala: 0-1 (normalizado)</span>
          </div>
        </div>
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
      </div>
    </button>

    {/* Cobertura_Lectores */}
    <button
      onClick={() => setMetricFilter('Cobertura_Lectores')}
      className={`group relative p-3 rounded-lg border-2 transition-all duration-200 text-left ${
        metricFilter === 'Cobertura_Lectores'
          ? 'border-orange-500 bg-orange-50 shadow-sm'
          : 'border-gray-200 bg-white hover:border-orange-300 hover:bg-orange-25'
      }`}
    >
      <div className="flex items-center space-x-3">
        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
          metricFilter === 'Cobertura_Lectores' ? 'bg-orange-500' : 'bg-orange-400'
        }`}></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <span className={`text-sm font-medium ${
              metricFilter === 'Cobertura_Lectores' ? 'text-orange-900' : 'text-gray-900'
            }`}>Cobertura</span>
            <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
            <span className="text-xs text-orange-600 font-medium">Alcance</span>
          </div>
          <p className="text-xs text-gray-600 truncate">Estudiantes que Leen</p>
        </div>
        {metricFilter === 'Cobertura_Lectores' && (
          <svg className="w-4 h-4 text-orange-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )}
      </div>
      
      {/* Tooltip */}
      <div className="absolute invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 z-10">
        <div className="bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg">
          <div className="font-semibold text-white mb-1 flex items-center space-x-1">
            <span>👥 Cobertura de Lectores</span>
          </div>
          <p className="text-gray-200 leading-relaxed">
            Porcentaje de estudiantes que participan activamente en el programa de lectura
          </p>
          <div className="flex items-center space-x-2 mt-2 pt-2 border-t border-gray-700">
            <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
            <span className="text-gray-300">Escala: 0-100%</span>
          </div>
        </div>
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
      </div>
    </button>

    {/* Lecturas_Promedio */}
    <button
      onClick={() => setMetricFilter('Lecturas_Promedio')}
      className={`group relative p-3 rounded-lg border-2 transition-all duration-200 text-left ${
        metricFilter === 'Lecturas_Promedio'
          ? 'border-red-500 bg-red-50 shadow-sm'
          : 'border-gray-200 bg-white hover:border-red-300 hover:bg-red-25'
      }`}
    >
      <div className="flex items-center space-x-3">
        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
          metricFilter === 'Lecturas_Promedio' ? 'bg-red-500' : 'bg-red-400'
        }`}></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <span className={`text-sm font-medium ${
              metricFilter === 'Lecturas_Promedio' ? 'text-red-900' : 'text-gray-900'
            }`}>Lect/Día</span>
            <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
            <span className="text-xs text-red-600 font-medium">Volumen</span>
          </div>
          <p className="text-xs text-gray-600 truncate">Lecturas por Día</p>
        </div>
        {metricFilter === 'Lecturas_Promedio' && (
          <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )}
      </div>
      
      {/* Tooltip */}
      <div className="absolute invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 z-10">
        <div className="bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg">
          <div className="font-semibold text-white mb-1 flex items-center space-x-1">
            <span>📖 Lecturas por Día</span>
          </div>
          <p className="text-gray-200 leading-relaxed">
            Promedio de lecturas realizadas diariamente por cada estudiante activo
          </p>
          <div className="flex items-center space-x-2 mt-2 pt-2 border-t border-gray-700">
            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
            <span className="text-gray-300">Unidades: lecturas/día</span>
          </div>
        </div>
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
      </div>
    </button>

    {/* Usuarios_Activos */}
    <button
      onClick={() => setMetricFilter('Usuarios_Activos')}
      className={`group relative p-3 rounded-lg border-2 transition-all duration-200 text-left ${
        metricFilter === 'Usuarios_Activos'
          ? 'border-indigo-500 bg-indigo-50 shadow-sm'
          : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-25'
      }`}
    >
      <div className="flex items-center space-x-3">
        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
          metricFilter === 'Usuarios_Activos' ? 'bg-indigo-500' : 'bg-indigo-400'
        }`}></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <span className={`text-sm font-medium ${
              metricFilter === 'Usuarios_Activos' ? 'text-indigo-900' : 'text-gray-900'
            }`}>Usuarios</span>
            <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
            <span className="text-xs text-indigo-600 font-medium">Participación</span>
          </div>
          <p className="text-xs text-gray-600 truncate">Usuarios Activos</p>
        </div>
        {metricFilter === 'Usuarios_Activos' && (
          <svg className="w-4 h-4 text-indigo-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )}
      </div>
      
      {/* Tooltip */}
      <div className="absolute invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 z-10">
        <div className="bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg">
          <div className="font-semibold text-white mb-1 flex items-center space-x-1">
            <span>👤 Usuarios Activos</span>
          </div>
          <p className="text-gray-200 leading-relaxed">
            Número de estudiantes que han realizado al menos una lectura en el período
          </p>
          <div className="flex items-center space-x-2 mt-2 pt-2 border-t border-gray-700">
            <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
            <span className="text-gray-300">Unidades: estudiantes</span>
          </div>
        </div>
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
      </div>
    </button>
  </div>
</div>









        <SelectorAnioMes
        value={anioMesSeleccionado}
        onChange={setAnioMesSeleccionado}
        />
        <MetricChart
          datosProcesados={datosProcesados}
          metricFilter={metricFilter}
          metricas={{
            IGL: { label: 'IGL', colorHex: '#0EA5E9', format: (v: number) => Number(v).toFixed(3) },
            ICP: { label: 'ICP', colorHex: '#7C3AED', format: (v: number) => Number(v).toFixed(3) },
            INorm_Promedio: { label: 'INorm', colorHex: '#22C55E', format: (v: number) => Number(v).toFixed(3) },
            Usuarios_Activos: { label: 'Usuarios activos', colorHex: '#111827', format: (v: number) => Math.round(v) },
            Cobertura_Lectores: { label: 'Cobertura Lectores', colorHex: '#F97316', format: (v: number) => Number(v).toFixed(1) },
            Lecturas_Promedio: { label: 'Lecturas promedio', colorHex: '#EF4444', format: (v: number) => Number(v).toFixed(1) },
          }}
          viewType={viewType}
          anioMesSeleccionado={anioMesSeleccionado}
          // maxValor={...} // opcional
        />


          



        
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 ">
      <HeaderCustomized nivel_estudiante={user.attributes['custom:Nivel']} grade_estudiante={user.attributes['custom:grade']} especialidad_usuario={user.attributes['custom:especialidad']}  categoria_usuario={user.attributes['custom:categoria_usuario']}  grado_estudiante={user.attributes['custom:Grado']} colegio_usuario={user.attributes['custom:Colegio']} username={user.attributes['given_name']} zoneinfo={user.attributes['zoneinfo']} ></HeaderCustomized>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          <div className="xl:col-span-1">
            <SelectorColegios />
          </div>
          <div className="xl:col-span-3">
            <DashboardProgreso />
          </div>
        </div>
      </div>
    </div>
  );
}
